import { AppConst } from 'src/app/shared/constants/app-const';
import { UrlConst } from 'src/app/shared/constants/url-const';
import { Sensor } from 'src/app/shared/interfaces/sensor';
import { Site } from 'src/app/shared/interfaces/site';
import { LoadingService } from 'src/app/shared/services/loading.service';

import {
  Component,
  EventEmitter,
  inject,
  Input,
  OnInit,
  Output,
} from '@angular/core';
import { UntypedFormBuilder, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';

import { SensorListTableService } from '../sensor-list-table/sensor-list-table.service';
import { SensorPhFormService } from './sensor-ph-form.service';

@Component({
  selector: 'app-sensor-ph-form',
  templateUrl: './sensor-ph-form.component.html',
  styleUrls: ['./sensor-ph-form.component.scss'],
})
export class SensorPhFormComponent implements OnInit {
  private formBuilder = inject(UntypedFormBuilder);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private loadingService = inject(LoadingService);
  private sensorPhFormService = inject(SensorPhFormService);
  private sensorListTableService = inject(SensorListTableService);

  constructor() {}

  @Input() sensor: Sensor | null = null;
  @Output() registered = new EventEmitter<boolean>();
  @Output() canceled = new EventEmitter<boolean>();

  errors: string[] = [];
  siteId: string | null = null;
  site: Site | null = null;
  sensors: Sensor[] | null = null;

  isUpdate: boolean = false;

  defaultMinPh = AppConst.DEFAULT_PH_MIN;
  defaultMaxPh = AppConst.DEFAULT_PH_MAX;

  form = this.formBuilder.group({
    sensorId: ['', [Validators.required]],
    positionName: ['', [Validators.required]],
    minPh: ['', [Validators.required, Validators.min(1), Validators.max(14)]],
    maxPh: ['', [Validators.required, Validators.min(1), Validators.max(14)]],
  });
  get sensorId() {
    return this.form.get('sensorId');
  }
  get positionName() {
    return this.form.get('positionName');
  }
  get minPh() {
    return this.form.get('minPh');
  }
  get maxPh() {
    return this.form.get('maxPh');
  }

  async ngOnInit() {
    this.sensorPhFormService.addLog();

    this.route.paramMap.subscribe((params) => (this.siteId = params.get('id')));

    // 管理者か一般ユーザのみアクセス可能
    if (!this.sensorPhFormService.isValidUser()) {
      console.log('user is not sysadmin or general');
      this.router.navigate([UrlConst.SLASH + UrlConst.PATH_SIGN_IN]);
      return;
    }

    // 一般ユーザの場合は、自分のサイトのみアクセス可能
    if (this.sensorPhFormService.isGeneralGroup()) {
      console.log('user is general');
      const account = this.sensorPhFormService.getUser();
      if (account === null) {
        console.log('account is null');
        this.router.navigate([UrlConst.SLASH + UrlConst.PATH_SIGN_IN]);
        return;
      }
      if (this.siteId !== account.user) {
        console.log('siteId != user');
        this.router.navigate([UrlConst.SLASH + UrlConst.PATH_SIGN_IN]);
        return;
      }
    }

    // サイトIDが指定されていない場合は、サイト一覧に戻る
    if (this.siteId === null) {
      this.router.navigate([UrlConst.SLASH + UrlConst.PATH_SITE_CONFIG]);
      return;
    }

    // サイト情報取得
    this.site = await this.sensorPhFormService.getSiteWithOrganization(
      this.siteId
    );

    // サイトが取得できなかった場合は、サイト一覧に戻る
    if (this.site === null) {
      this.router.navigate([UrlConst.SLASH + UrlConst.PATH_SITE_CONFIG]);
      return;
    }

    // フォームのプルダウンリスト作成（未使用センサー）
    const sensors: Sensor[] | null =
      await this.sensorPhFormService.getNoConfigSensorsWithOrganization();

    this.sensors = sensors;

    // センサーが指定されていたら更新モード
    if (
      this.sensor !== null &&
      this.sensor.id !== undefined &&
      this.sensors !== null
    ) {
      this.isUpdate = true;

      // 未使用センサーに今のセンサーを追加する
      const usedSensor = await this.sensorPhFormService.getSensor(
        this.sensor.id
      );
      this.sensors.unshift(usedSensor!);

      this.form.setValue({
        sensorId: this.sensor.id,
        positionName: this.sensor.positionName ? this.sensor.positionName : '',
        minPh: this.sensor.minPh ? this.sensor.minPh : AppConst.DEFAULT_PH_MIN,
        maxPh: this.sensor.maxPh ? this.sensor.maxPh : AppConst.DEFAULT_PH_MAX,
      });
    } else {
      this.isUpdate = false;
      this.form.patchValue({
        minPh: AppConst.DEFAULT_PH_MIN.toString(),
        maxPh: AppConst.DEFAULT_PH_MAX.toString(),
      });
    }
  }

  async onSubmit() {
    try {
      this.loadingService.startLoading();

      // 更新でIDが変わっている場合は一旦古いセンサーを削除する
      if (this.isUpdate) {
        if (this.sensor === null) {
          this.registered.emit(false);
          return;
        }

        // センサー変更の時は一旦今のセンサー情報を削除して、後に控える登録処理を実行する
        if (this.sensorId?.value !== this.sensor.id) {
          try {
            await this.sensorListTableService.deleteSensor(
              this.site!,
              this.sensor!
            );
          } catch (error) {
            console.log('SiteConfigPageComponent.deleteConfig(): ', error);
            return;
          }
        }
      }

      // 設置場所の文言だけの変更
      await this.sensorPhFormService.setSensorToSite(
        this.site!,
        this.sensorId?.value,
        this.positionName?.value,
        this.minPh?.value,
        this.maxPh?.value
      );

      this.registered.emit(true);
      if (this.isUpdate === true) {
        this.isUpdate = false;
        this.sensor = null;
      }
      this.form.reset();
    } catch (error) {
      console.error(error);
    } finally {
      this.loadingService.stopLoading();
    }
  }

  cancel() {
    this.isUpdate = false;
    this.sensor = null;

    this.canceled.emit(true);
  }
}
